import type { Quest, QuestSection, QuestType } from '@/lib/types';

interface SequenceStep {
  /** Backend quest `code` this step is known by. */
  slug: string;
  /** Fallback match when the admin catalogue uses another code for the step. */
  type?: QuestType;
}

/**
 * Onboarding quests that must be done strictly in this order. A quest is
 * matched to a step by `slug` (the backend quest `code`; `connect-telegram-bot`
 * is the client-synthesized rail2 card from `useTelegramBotQuest`), else by
 * `type` — quest codes are free text in the admin back-office (production's X
 * quest is `follow_x_true`), so the code alone can't be trusted to identify
 * the step. Each step stays locked until the previous one present in the
 * catalogue is completed. Discord is last, so nothing waits on it — it's
 * optional.
 *
 * Frontend-only: bigw-api still accepts claims in any order. A quest matching
 * no step simply isn't chained.
 */
export const ONBOARDING_SEQUENCE: readonly SequenceStep[] = [
  { slug: 'visit_homepage' },
  { slug: 'follow_x', type: 'twitter_follow' },
  { slug: 'join_telegram', type: 'telegram_join' },
  { slug: 'connect-telegram-bot' },
  { slug: 'join_discord', type: 'discord_join' },
];

/**
 * Staging escape hatch: `VITE_SKIP_TELEGRAM_JOIN_STEP=true` stops
 * "Join our Telegram community" from blocking the steps after it (Telegram
 * linking isn't available on staging), so the bot unlocks right after X. The
 * Telegram card itself still unlocks in order. Unset in production.
 */
const NON_BLOCKING_STEPS: ReadonlySet<string> = new Set(
  import.meta.env.VITE_SKIP_TELEGRAM_JOIN_STEP === 'true' ? ['join_telegram'] : [],
);

/** Position of the quest's step in `ONBOARDING_SEQUENCE`, -1 when unchained. */
function stepIndex(quest: Quest): number {
  const bySlug = ONBOARDING_SEQUENCE.findIndex((step) => step.slug === quest.slug);
  if (bySlug !== -1) return bySlug;
  return ONBOARDING_SEQUENCE.findIndex((step) => step.type === quest.type);
}

function sortIndex(quest: Quest): number {
  const index = stepIndex(quest);
  return index === -1 ? Number.POSITIVE_INFINITY : index;
}

/**
 * Sorts the onboarding section into `ONBOARDING_SEQUENCE` order (other
 * onboarding quests keep their relative order after the chain) and locks
 * every available step whose predecessor isn't completed yet. Completed
 * quests are never re-locked.
 */
export function applyOnboardingSequence(sections: QuestSection[]): QuestSection[] {
  return sections.map((section) => {
    if (section.category !== 'onboarding') return section;

    // Array.prototype.sort is stable, so non-chain quests keep their order.
    const sorted = [...section.quests].sort((a, b) => sortIndex(a) - sortIndex(b));

    let previous: Quest | undefined;
    const quests = sorted.map((quest) => {
      const index = stepIndex(quest);
      if (index === -1) return quest;
      const blocker = previous;
      if (!NON_BLOCKING_STEPS.has(ONBOARDING_SEQUENCE[index].slug)) previous = quest;
      if (!blocker || blocker.status === 'completed' || quest.status !== 'available') {
        return quest;
      }
      return {
        ...quest,
        status: 'locked' as const,
        lockedBy: blocker.title,
        requirementHint: `Complete "${blocker.title}" first`,
      };
    });

    return { ...section, quests };
  });
}

/**
 * Makes "Connect Telegram Bot" a prerequisite of every bonus quest. The bot
 * card is client-synthesized from rail2 (see `useTelegramBotQuest`), so
 * bigw-api can neither list it in `required_quest_codes` nor check it: it's
 * appended to the checklist here, and an otherwise-unlocked bonus stays locked
 * until the bot is linked. Must run after `mergeSyntheticOnboardingQuest`,
 * which is what puts the bot card in `sections`; without one (rail2 not
 * configured) no bonus is gated.
 *
 * Frontend-only, like the onboarding order: bigw-api still accepts the claim
 * once its own prerequisites are met.
 *
 * Also restores the invariant the UI relies on — `prerequisites` only on a
 * locked bonus — since `adaptQuest` resolves them for unlocked ones too.
 */
export function applyBonusBotGate(sections: QuestSection[]): QuestSection[] {
  const bot = sections
    .flatMap((section) => section.quests)
    .find((quest) => quest.type === 'telegram_bot_connect');

  return sections.map((section) => {
    if (section.category !== 'bonus') return section;

    const quests = section.quests.map((quest) => {
      if (quest.type !== 'bonus' || quest.status === 'completed') return quest;

      const botDone = !bot || bot.status === 'completed';
      if (quest.status !== 'locked' && botDone) {
        return { ...quest, prerequisites: undefined };
      }
      if (!bot) return quest;

      const prerequisites = [
        ...(quest.prerequisites ?? []),
        { code: bot.slug, title: bot.title, completed: botDone },
      ];
      const todo = prerequisites.filter((p) => !p.completed).map((p) => p.title);
      return {
        ...quest,
        status: 'locked' as const,
        prerequisites,
        requirementHint: todo.length ? `Complete first: ${todo.join(', ')}` : quest.requirementHint,
      };
    });

    return { ...section, quests };
  });
}
